// @flow
import React, { type AbstractComponent } from 'react'
import classnames from 'classnames'
import type { StudentSpecificContextualDataEntry } from '@mission.io/mission-toolkit'

import TextToSpeech from '../TextToSpeech/index.jsx'

import { SvgIcon } from '../../juniorPlusStations/Sensor/Map/Map.jsx'
import { FaMagnifyingGlass as InvestigateIcon } from 'react-icons/fa6'

import { useExpandedDataContext, type ContextualDataLookup } from './ExpandedDataProvider.jsx'
import type { OpenContext } from '../LiteracyEvent/helperComponents/TextEventInput.jsx'
import { useSelector } from 'react-redux'
import { getActiveStudentCount } from '../../store/stores/general'

export const GRID_AREA_KEY_WORDS = {
	vote: 'vote',
	importance: 'importance',
}

export type MediaModalOverrides = {
	overlayStyle: {| zIndex: number |},
	frameContent: boolean,
}

type Props = {
	className?: string,
	style?: { [string]: mixed },
	allowVote?: boolean,
	contextualData: StudentSpecificContextualDataEntry,
	onHover?: () => void,
	onBlur?: () => void,
	smallScreenSize?: string,
	lookup: ContextualDataLookup,

	mediaModalOverrides?: ?MediaModalOverrides,
	openContext: OpenContext,
}

/**
 * The ContextualDataCard component displays a single piece of contextual data. It includes the data, the current class and student vote, and a way to view any media.
 * It can trigger the expanded data view when clicked. IMPORTANT: If this component is not rendered within the ExpandedDataProvider context, the expanded data view will not appear.
 * @param props
 * @param props.openContext The context in which the contextual data card is being opened. e.g. 'MAP', 'CULMINATING_MOMENT', 'CREATIVE_CANVAS'
 */
export const ContextualDataCard = (React.forwardRef(function ContextualDataCard(
	{
		className,
		contextualData,
		lookup,
		onHover,
		onBlur,
		smallScreenSize,
		mediaModalOverrides,
		allowVote = true,
		openContext,
		...props
	}: Props,
	ref
) {
	const canExpand = contextualData.text.length > 0 || contextualData.media.length > 0

	const id = React.useId()
	const { expandedData, openExpandedData } = useExpandedDataContext() ?? {}
	const totalStudents = useSelector(getActiveStudentCount)
	const percentage = contextualData.classVote / totalStudents

	return (
		<div className="w-full min-w-200px" id={id}>
			<button
				{...props}
				// add a custom label for the button here because this button is a card and has lots of content (title, subtitle, icon)
				aria-label={contextualData.title}
				className={classnames(
					className,
					'shadow-primary-800 shadow-md cursor-pointer text-left transition-all bg-[#5c6992] w-full min-w-200px rounded p-4',
					'hover:scale-[1.025] hover:shadow-primary-900 hover:shadow-lg'
				)}
				ref={ref}
				onClick={event => {
					canExpand && openExpandedData?.({ lookup, allowVote, openContext })
					onBlur?.() // Remove line being drawn to the map when card is hovered over
				}}
				onMouseEnter={() => onHover?.()}
				onTouchStart={() => onHover?.()}
				onMouseLeave={() => onBlur?.()}>
				<div className="flex gap-4">
					{contextualData.icon && (
						<SvgIcon
							{...{
								url: contextualData.icon,
								className: 'size-8 m-auto',
								width: 32,
								height: 32,
							}}
						/>
					)}
					<div className="flex flex-1 justify-between">
						<div>
							<h4 className="flex-1">
								<TextToSpeech>{contextualData.title}</TextToSpeech>
							</h4>
							{contextualData.subtitle && (
								<p className="text-white pt-2 -mt-1">
									<TextToSpeech>{contextualData.subtitle}</TextToSpeech>
								</p>
							)}
						</div>

						<div className="flex gap-5 items-end">
							{allowVote && contextualData.studentVote === null ? (
								<div
									className={classnames(
										'self-start size-8 rounded-full bg-warning',
										lookup.id !== expandedData?.lookup.id
											? 'animate-pulse-shadow	 [--pulse-shadow-from:theme(colors.warning/100%)] [--pulse-shadow-to:theme(colors.warning/100%)]'
											: null
									)}>
									<InvestigateIcon
										className={classnames('self-start text-primary-900 size-8 p-2')}
									/>
								</div>
							) : // Do not show a circle with 0 in it if the class vote score is 0 (this is mainly to avoid a bunch of zeroes at the beginning when data shows up)
							contextualData.classVote !== 0 ? (
								<div
									title="Class Score"
									role="img"
									aria-label={`Class vote score: ${contextualData.classVote} out of ${totalStudents} students`}
									className={classnames(
										percentage > 0.6
											? '[--relevancy-color:theme(colors.accent-blue)]'
											: percentage > 0.3
											? '[--relevancy-color:theme(colors.white)]'
											: '[--relevancy-color:theme(colors.accent-orange)]',
										'text-primary-200 self-start flex flex-col ',
										'size-7 rounded-full border-2 border-[var(--relevancy-color)] text-[var(--relevancy-color)] flex items-center justify-center'
									)}>
									{contextualData.classVote}
								</div>
							) : null}
						</div>
					</div>
				</div>
			</button>
		</div>
	)
}): AbstractComponent<Props, HTMLButtonElement>)
